import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { NgOptimizedImage } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  inject,
  linkedSignal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { map } from 'rxjs';

import { UiIconComponent } from '../../shared/ui/icon/icon.component';
import { CustomerProfileService } from '../auth/customer-profile.service';
import { CustomerSessionService } from '../auth/customer-session.service';
import { CUSTOMER_TYPE } from '../auth/customer-type';
import { EMPIRIA_CONTACT } from '../contact/empiria-contact';
import { APP_PATHS } from '../routing/app-paths';

/**
 * Shell of the authenticated panel: responsive sidenav (side on desktop, over on mobile),
 * toolbar and routed content. Nav sections arrive with slices S-02+; the route sits
 * behind `customerAuthGuard` since S-01.
 */
@Component({
  selector: 'app-panel-layout',
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    MatButtonModule,
    MatSidenavModule,
    MatToolbarModule,
    UiIconComponent,
    NgOptimizedImage,
  ],
  template: `
    <!-- The skip-link's look lives in src/styles/_a11y.scss, shared with the public
         layout through the a11y.skip-link mixin below. Only the behaviour stays here:
         href="#main-content" would resolve against <base href="/"> and leave the current
         route, so the handler moves focus itself; the href stays for link semantics. -->
    <a class="skip-link" href="#main-content" (click)="skipToContent($event, main)">
      Przejdź do treści
    </a>
    <mat-sidenav-container>
      <!-- 'fixedInViewport' TYLKO W TRYBIE NAKŁADKI, i to jest warunek MIERZONY, nie
           gust. Bez niego MatSidenav rozciąga szufladę na wysokość CAŁEGO DOKUMENTU
           (zmierzone 2026-08-17 na /panel/szkolenia przy 390 px: 1549 px przy oknie
           wysokim na 844 px), więc dno szuflady leży 700 px pod dolną krawędzią ekranu.
           W trybie szyny warunek jest fałszywy świadomie: szyna ma przewijać się razem
           ze stroną tak, jak przewijała się do tej pory. -->
      <mat-sidenav
        [mode]="sidenavMode()"
        [fixedInViewport]="sidenavMode() === 'over'"
        [class.is-overlay]="sidenavMode() === 'over'"
        [opened]="sidenavOpened()"
        (openedChange)="sidenavOpened.set($event)"
      >
        <!-- TAFLA SZUFLADY. Węzeł powstał, gdy na dnie szuflady stała karta użytkownika
             i kolumna flex musiała żyć POD '.mat-drawer-inner-container' Materiala
             (hostowa nie sięgała rzutowanej treści). P11 zabrał kartę do górnego paska,
             ale wspólny rodzic zostaje: to jedyne miejsce na układ wnętrza szuflady bez
             '::ng-deep' do cudzego wnętrza (tego repozytorium nie ma ani jednego takiego
             selektora i nie zaczyna tutaj), a przestylowanie z punktu 1 planu poprawek
             będzie go potrzebować z powrotem. -->
        <div class="drawer-pane">
          <!-- STREFA MARKI. Znak jest PLIKIEM Z PANELU ADMINA ('public/logo.png', kopia
               'front/src/assets/images/logo.png'), a nie rysunkiem składanym z tokenów:
               marka klienta i marka biura mają być tym samym obrazkiem, a nie dwiema
               interpretacjami tego samego pomysłu.

               CZYTNIK EKRANU DOSTAJE JĄ RAZ: nazwę niesie tekst alternatywny obrazu, a
               stojący pod nim napis „Panel klienta" mówi co INNEGO — czym jest ten ekran,
               a nie czyj jest. Wcześniej napis „EMPIRIA" dublował sens znaku i dlatego
               znak był wtedy 'aria-hidden'; teraz nie ma czego chować.

               'NgOptimizedImage' z wymiarami WŁASNYMI pliku (190×49) — z nich liczy się
               proporcja i rezerwa miejsca. Bez 'priority', z tego samego powodu co
               w powłoce publicznej: logo o wysokości 32 px nie jest elementem LCP. -->
          <div class="brand-zone">
            <span class="brand-mark">
              <img ngSrc="/logo.png" width="190" height="49" alt="Salon Edukacyjny EMPIRIA" />
              <span class="brand-name-secondary">Panel klienta</span>
            </span>
            <!-- KRZYŻYK ISTNIEJE TYLKO TAM, GDZIE CZYMKOLWIEK STERUJE — ten sam warunek
                 i to samo uzasadnienie co przy hamburgerze niżej: gdy szuflada stoi jako
                 szyna, nie ma czego zamykać, a kontrolka opisująca niewykonalną akcję jest
                 kłamstwem, nie ozdobą. Makieta 'moje_szkolenia_mobile_edupanel' stawia go
                 w pasku szuflady po prawej, naprzeciw znaku marki.

                 DRUGIE WYJŚCIE, NIE JEDYNE: nakładkę zamyka też kliknięcie w przyciemnione
                 tło i klawisz Escape (jedno i drugie robi MatSidenav sam, a fokus wraca
                 wtedy na hamburger). Krzyżyk dokłada wyjście WIDOCZNE — dwa pozostałe
                 trzeba znać. -->
            @if (sidenavMode() === 'over') {
              <button
                mat-icon-button
                class="icon-button drawer-close"
                type="button"
                aria-label="Zamknij menu sekcji panelu"
                (click)="closeSidenav()"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75"
                     stroke-linecap="round" aria-hidden="true">
                  <path d="m6 6 12 12M18 6 6 18" />
                </svg>
              </button>
            }
          </div>
          <nav aria-label="Sekcje panelu">
            <!-- Nagłówek zostaje w drzewie dostępności, znika z ekranu: strefa marki nad
                 nawigacją mówi to samo wzrokowo, a dwa razy ten sam napis to szum dla
                 czytnika. Sekcje NIE mają już własnego widocznego tytułu. -->
            <h2 class="visually-hidden">Sekcje panelu</h2>
            <!-- Kolejne sekcje dopisują tu własne pozycje. WARUNKOWE są dokładnie te,
                 które stoją w @if, i obie z tego samego powodu: „Moje abonamenty"
                 (S-11) i „Zapotrzebowanie na szkolenie" (S-10) są funkcjami PLACÓWKI,
                 więc klient indywidualny dostałby po kliknięciu 403 z backendu. Obie
                 czytają ten sam customerType, a nieznany typ (żądanie profilu w locie
                 albo nieudane) też ukrywa — pozycja, która pojawia się i znika, jest
                 dla czytnika ekranu gorsza niż spóźniona. Każda pozycja BEZ @if jest
                 bezwarunkowa, bo jej ekran istnieje dla obu typów konta.

                 Ukrycie jest uprzejmością w prezentacji, a nie granicą dostępu: tę
                 egzekwuje backend (403), a dla zapotrzebowania dodatkowo
                 establishmentOnlyGuard na trasie.

                 ŚWIADOMIE BEZ SUMY POZYCJI: enumeracja warunków się nie starzeje,
                 a liczba zestarzeje się przy pierwszym sąsiednim slice'ie dokładającym
                 pozycję do tego samego <ul>. -->
            <!-- IKONY SĄ OZDOBĄ, KAŻDA Z 'aria-hidden': nazwa sekcji stoi obok w tekście,
                 więc czytnik ekranu nie czyta nic dwa razy, a rozpoznawalność pozycji nie
                 zależy od tego, czy ktoś rozpoznaje piktogram. Wektory są WPISANE w szablon,
                 bo font ikon (Material Symbols) znaczyłby żądanie do Google Fonts, którego
                 ta aplikacja świadomie nie robi (src/styles/_fonts.scss) — i wpis w
                 'src/index.html', którego ta faza nie ma prawa tknąć. -->
            <ul>
              <!-- Kalendarz jest PIERWSZY, bo jest dashboardem panelu (P2): to na niego
                   przekierowuje /panel. Tuż pod nim Kalendarium — dawny publiczny
                   katalog szkoleń, przeniesiony za logowanie pod nową nazwą. -->
              <li>
                <a
                  [routerLink]="['/', panelPath, calendarPath]"
                  routerLinkActive="is-active"
                  ariaCurrentWhenActive="page"
                  (click)="handleNavigation(main)"
                >
                  <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"
                       aria-hidden="true">
                    <rect x="3" y="5" width="18" height="16" rx="2" />
                    <path d="M3 10h18M8 3v4M16 3v4" />
                  </svg>
                  Kalendarz
                </a>
              </li>
              <li>
                <a
                  [routerLink]="['/', panelPath, calendariumPath]"
                  routerLinkActive="is-active"
                  ariaCurrentWhenActive="page"
                  (click)="handleNavigation(main)"
                >
                  <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"
                       aria-hidden="true">
                    <path d="M12 6c-1.6-1.4-3.8-2.1-6.5-2.1v13.6c2.7 0 4.9.7 6.5 2.1
                             1.6-1.4 3.8-2.1 6.5-2.1V3.9c-2.7 0-4.9.7-6.5 2.1Z" />
                    <path d="M12 6v13.6" />
                  </svg>
                  Kalendarium
                </a>
              </li>
              <li>
                <a
                  [routerLink]="['/', panelPath, myCoursesPath]"
                  routerLinkActive="is-active"
                  ariaCurrentWhenActive="page"
                  (click)="handleNavigation(main)"
                >
                  <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"
                       aria-hidden="true">
                    <path d="M12 3 2 8l10 5 10-5-10-5Z" />
                    <path d="M6 11.5V16c0 1.4 2.7 2.5 6 2.5s6-1.1 6-2.5v-4.5" />
                  </svg>
                  Moje szkolenia
                </a>
              </li>
              <li>
                <a
                  [routerLink]="['/', panelPath, myRegistrationsPath]"
                  routerLinkActive="is-active"
                  ariaCurrentWhenActive="page"
                  (click)="handleNavigation(main)"
                >
                  <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"
                       aria-hidden="true">
                    <path d="M9 4h6v3H9z" />
                    <path d="M15 5.5h2A1.5 1.5 0 0 1 18.5 7v12A1.5 1.5 0 0 1 17 20.5H7A1.5 1.5 0
                             0 1 5.5 19V7A1.5 1.5 0 0 1 7 5.5h2" />
                    <path d="M8.5 11.5h7M8.5 15.5h4.5" />
                  </svg>
                  Moje zgłoszenia
                </a>
              </li>
              <li>
                <a
                  [routerLink]="['/', panelPath, certificatesPath]"
                  routerLinkActive="is-active"
                  ariaCurrentWhenActive="page"
                  (click)="handleNavigation(main)"
                >
                  <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"
                       aria-hidden="true">
                    <circle cx="12" cy="9" r="5" />
                    <path d="M8.5 13.2 7 21l5-2.4 5 2.4-1.5-7.8" />
                  </svg>
                  Moje certyfikaty
                </a>
              </li>
              @if (showSubscriptions()) {
                <li>
                  <a
                    [routerLink]="['/', panelPath, mySubscriptionsPath]"
                    routerLinkActive="is-active"
                    ariaCurrentWhenActive="page"
                    (click)="handleNavigation(main)"
                  >
                    <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                         stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"
                         aria-hidden="true">
                      <rect x="3" y="5.5" width="18" height="13" rx="2" />
                      <path d="M3 10h18M7 14.5h3" />
                    </svg>
                    Moje abonamenty
                  </a>
                </li>
              }
              @if (showClosedCourseRequest()) {
                <li>
                  <a
                    [routerLink]="['/', panelPath, closedCourseRequestPath]"
                    routerLinkActive="is-active"
                    ariaCurrentWhenActive="page"
                    (click)="handleNavigation(main)"
                  >
                    <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                         stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"
                         aria-hidden="true">
                      <path d="M14 3.5H7A1.5 1.5 0 0 0 5.5 5v14A1.5 1.5 0 0 0 7 20.5h10a1.5 1.5 0
                               0 0 1.5-1.5V8z" />
                      <path d="M14 3.5V8h4.5" />
                      <path d="M12 11.5v5M9.5 14h5" />
                    </svg>
                    Zapotrzebowanie na szkolenie
                  </a>
                </li>
              }
              <li>
                <a
                  [routerLink]="['/', panelPath, myProfilePath]"
                  routerLinkActive="is-active"
                  ariaCurrentWhenActive="page"
                  (click)="handleNavigation(main)"
                >
                  <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"
                       aria-hidden="true">
                    <circle cx="12" cy="8" r="3.75" />
                    <path d="M5 20a7 7 0 0 1 14 0" />
                  </svg>
                  Mój profil
                </a>
              </li>
              <li>
                <a
                  [routerLink]="['/', panelPath, notificationsPath]"
                  routerLinkActive="is-active"
                  ariaCurrentWhenActive="page"
                  (click)="handleNavigation(main)"
                >
                  <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"
                       aria-hidden="true">
                    <path d="M18 9a6 6 0 1 0-12 0c0 5-2 6.5-2 6.5h16S18 14 18 9" />
                    <path d="M10.3 19a2 2 0 0 0 3.4 0" />
                  </svg>
                  Powiadomienia
                </a>
              </li>
              <!-- Kontakt (S-17) — do P2 pozycja headera publicznego, teraz sekcja
                   panelu. BEZ @if: dane biura ogląda każde konto. -->
              <li>
                <a
                  [routerLink]="['/', panelPath, contactPath]"
                  routerLinkActive="is-active"
                  ariaCurrentWhenActive="page"
                  (click)="handleNavigation(main)"
                >
                  <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"
                       aria-hidden="true">
                    <rect x="3" y="5.5" width="18" height="13" rx="2" />
                    <path d="m3.5 7.5 8.5 6 8.5-6" />
                  </svg>
                  Kontakt
                </a>
              </li>
              <!-- „Zmiana hasła" ZNIKŁA Z MENU (P9): formularz stoi teraz jako karta na
                   dole „Mojego profilu", a drugi wpis w menu na pojęcie, które klient
                   ogląda w profilu, to koszt nawigacyjny bez wymagania w FR — ten sam
                   precedens co zmiana adresu logowania, której w menu nie było nigdy. -->
            </ul>
          </nav>
        </div>
      </mat-sidenav>
      <mat-sidenav-content>
        <header>
          <mat-toolbar>
            <!-- HAMBURGER ISTNIEJE TYLKO TAM, GDZIE CZYMKOLWIEK STERUJE. Gdy szuflada
                 stoi jako szyna ('sidenavMode() === side', czyli na szerokim ekranie),
                 nawigacja jest widoczna w całości i przycisk „Menu" otwierałby to, co
                 i tak jest otwarte — makiety panelu nie mają go tam ani jednej.
                 Warunek czyta TRYB SZUFLADY, a nie próg z arkusza: to tryb rozstrzyga,
                 czy jest co przełączać, i to on rozstrzyga o zachowaniu przycisku.
                 Ukrycie samym 'display: none' zabrałoby przycisk z ekranu i z tabulacji,
                 ale zostawiłoby w kodzie kontrolkę opisującą stan, którego na tym ekranie
                 nie da się zmienić — a to jest kłamstwo, nie optymalizacja.

                 Przycisk pozostaje przy 'aria-expanded' związanym ze stanem szuflady —
                 to jedyny precedens tego atrybutu w repo. Ikona zastąpiła napis „Menu",
                 więc nazwę dostępną niesie teraz 'aria-label': bez niego przycisk
                 zostałby bez nazwy, co jest naruszeniem, a nie kwestią gustu. -->
            @if (sidenavMode() !== 'side') {
              <button
                mat-icon-button
                class="icon-button menu-toggle"
                type="button"
                aria-label="Menu sekcji panelu"
                [attr.aria-expanded]="sidenavOpened()"
                (click)="toggleSidenav()"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75"
                     stroke-linecap="round" aria-hidden="true">
                  <path d="M4 7h16M4 12h16M4 17h16" />
                </svg>
              </button>
            }
            <!-- Marka w pasku TYLKO na wąskim ekranie: tam szuflada jest nakładką i po
                 zamknięciu strefa marki sidenava znika z widoku. Na szerokim ekranie
                 powtarzałaby to, co i tak stoi nad nawigacją.

                 TEKST ALTERNATYWNY JEST PUSTY, i to jest różnica wobec obu pozostałych
                 wystąpień znaku: na wąskim ekranie ten sam znak stoi drugi raz w tym
                 samym dokumencie (w otwartej szufladzie), a dwa razy przeczytana nazwa
                 firmy to szum. Pusty 'alt' zdejmuje obraz z drzewa dostępności zamiast
                 zostawiać go bez nazwy — to jedyny sposób zgodny z WCAG 1.1.1. -->
            <span class="brand">
              <img ngSrc="/logo.png" width="190" height="49" alt="" />
            </span>
            <!-- Dzwonek POZA elementem '<nav>' i to jest celowe: skrót do jednej sekcji
                 nie jest nawigacją strony, a 'nav a' panelu ma pozostać zbiorem pozycji
                 menu (czyta go wygenerowany spec 'verify:s10-p3'). -->
            <a
              mat-icon-button
              class="icon-button"
              [routerLink]="['/', panelPath, notificationsPath]"
              aria-label="Powiadomienia"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75"
                   stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M18 9a6 6 0 1 0-12 0c0 5-2 6.5-2 6.5h16S18 14 18 9" />
                <path d="M10.3 19a2 2 0 0 0 3.4 0" />
              </svg>
            </a>
            <!-- WYLOGOWANIE WRÓCIŁO DO PASKA (P11, „Poprawki do panelu klienta"): karta
                 użytkownika na dnie szuflady poszła do kosza w całości, więc wyjście
                 z sesji stoi tu, obok dzwonka — i jak dzwonek POZA '<nav>', bo akcja
                 konta nie jest pozycją nawigacji ('nav a' czyta wygenerowany spec
                 'verify:s10-p3'). Ikona bez napisu z tego samego powodu co dzwonek:
                 pasek trzyma same skróty, a nazwę dostępną niesie 'aria-label'.
                 Pasek po lewej zostaje PUSTY i to jest stan docelowy: globalna
                 wyszukiwarka z makiety została odrzucona jako funkcja niezamawiana. -->
            <button
              mat-icon-button
              class="icon-button"
              type="button"
              aria-label="Wyloguj się"
              (click)="logout()"
            >
              <app-ui-icon name="wyloguj" />
            </button>
          </mat-toolbar>
        </header>
        <main id="main-content" tabindex="-1" #main>
          <router-outlet />
        </main>
        <!-- RZĄD PRAW AUTORSKICH (P3, „Poprawki do panelu klienta") — przeniesiony
             ze stopki layoutu publicznego, gdy część publiczna przestała istnieć.
             Sama linijka copyright, bez strefy kolumn: tamta linkowała ekrany,
             które teraz siedzą w sidenavie obok. -->
        <footer>
          <p class="footer-copyright">© {{ currentYear }} {{ contact.companyName }}</p>
        </footer>
      </mat-sidenav-content>
    </mat-sidenav-container>
  `,
  styles: `
    @use 'a11y';
    @use 'breakpoints' as bp;

    /* ŻADNEJ SUROWEJ DŁUGOŚCI W TYM BLOKU — pilnuje tego krok 1 bramki
       'npm run verify:s22-p2', z allowlistą na wartości, które nie są miarą niczego
       ('0', '1px', '100%', '50%', '100vh', '100dvh', 'auto'). Każdy odstęp, promień i
       stopień pisma czyta się z warstwy tokenów fazy 1; ta faza NIE deklaruje ani
       jednego tokenu. */

    :host {
      display: block;
      min-height: 100dvh;
    }

    @include a11y.skip-link;
    @include a11y.visually-hidden;

    /* Kanwa panelu jest CIEMNIEJSZA od chromu (sidenav i pasek stoją na 'surface',
       treść na 'surface-container'). Odwrotnie niż w Materialu domyślnie i celowo:
       kafle treści faz 3-4 mają się od czegoś odcinać, a para
       'on-surface' / 'surface-container' jest zmierzona w rejestrze kontrastu
       (scripts/design-system/token-pairs.mjs), więc nie jest to dobór na oko. */
    /* WYSOKOŚĆ STAŁA, nie minimalna — i to jest cała mechanika „przybitego chromu"
       (poprawka po przeglądzie Filipa). Materialowy '.mat-drawer-content' ma OD ZAWSZE
       'overflow: auto', ale dopóki kontener rósł z dokumentem, ten scrollport nigdy
       nie przewijał — przewijał dokument, więc lepki nagłówek kleił się do scrollportu,
       który stał w miejscu, i odjeżdżał razem z szufladą. Przy 100dvh na kontenerze
       przewija się WYŁĄCZNIE treść wewnątrz '.mat-drawer-content': pasek (sticky) stoi
       u góry, szuflada trzyma pełną wysokość okna (długa nawigacja przewija się w jej
       własnym 'overflow-y: auto'), a tryb nakładki na telefonie zostaje bez zmian. */
    mat-sidenav-container {
      height: 100dvh;
      background: var(--mat-sys-surface-container);
    }

    /* KOLUMNA NA CAŁĄ WYSOKOŚĆ OKNA (P3): od kiedy panel ma stopkę, przy krótkiej
       treści musi ona siedzieć na dole ekranu, nie tuż pod treścią — 'main' z
       'flex: 1' zjada wolną wysokość, dokładnie jak w layoucie publicznym. */
    mat-sidenav-content {
      display: flex;
      flex-direction: column;
      min-height: 100dvh;
      background: var(--mat-sys-surface-container);
      /* Ta kolumna jest teraz JEDYNYM scrollerem panelu, więc druga połowa rezerwy
         widgetu dostępności („dokąd przewinąć fokus", WCAG 2.2 SC 2.4.11) musi stać
         tutaj — globalny 'scroll-padding' na html rozstrzyga scroll DOKUMENTU, który
         w panelu już się nie porusza. Suma tokenów jak w src/styles.scss. */
      scroll-padding-block-end: calc(var(--empiria-space-3xl) + var(--empiria-space-sm));
    }

    mat-sidenav {
      /* Token kształtu, nie 'border-radius' na wnętrzu Materiala: M3 zaokrągla
         '.mat-drawer' po prawej ('--mat-sidenav-container-shape' →
         '--mat-sys-corner-large'), a szyna panelu ma stać równo z paskiem — bez
         zaokrągleń (poprawka po przeglądzie Filipa). */
      --mat-sidenav-container-shape: 0;

      width: var(--empiria-sidenav-width);
      border-right: 1px solid var(--mat-sys-outline-variant);
      background: var(--mat-sys-surface);
    }

    /* BIAŁA TAFLA WYŁĄCZNIE W TRYBIE NAKŁADKI, i to rozróżnienie jest decyzją, nie
       niedokończeniem. Makieta mobilna rysuje szufladę jako CZYSTĄ BIEL unoszącą się nad
       przyciemnioną treścią — biel jest tam sygnałem „to jest osobna tafla nad stroną".
       Jako szyna na szerokim ekranie ta sama szuflada nie unosi się nad niczym: stoi
       w jednym rzędzie z paskiem narzędzi i ma z nim trzymać jedną barwę chromu
       ('surface'), inaczej rozjechałaby się z paskiem o dwa tony w poziomie.
       Biel czyta się z roli 'surface-container-lowest' — tej samej, którą Q-221
       (decyzja Filipa, 2026-08-17) przywrócił kartom szczegółów i karcie widgetu; ta faza
       NIE deklaruje nowej roli koloru ani nowej pary w rejestrze kontrastu. */
    mat-sidenav.is-overlay {
      background: var(--mat-sys-surface-container-lowest);
    }

    /* KONSUMENT OBU TOKENÓW MARKI W POWŁOCE PANELU — krok 3 verify:s22-p1 wymaga tego
       PER TOKEN i właśnie w core/layout/**, bo obie powłoki opakowują każdą trasę.
       Znak jest teraz rastrem i nie słucha żadnego tokenu (tryb wysokiego kontrastu
       widgetu dostępności zostawi go bez zmian — skala szarości NIE, bo tamta działa
       globalnym 'filter' na 'html', a nie podmianą tokenów), więc zieleń marki
       przenosi się na dwa elementy, które tokenom podlegają:
       · kreska zamykająca strefę marki — powierzchnia nietekstowa, --empiria-brand-accent
         (3,67:1 na 'surface', próg 3:1 z WCAG 1.4.11), grubość 1px z allowlisty kroku 1
         verify:s22-p2;
       · podpis „Panel klienta" — TEKST, więc --empiria-brand-text (5,05:1 na 'surface',
         próg 4,5:1). Odwrotnie nie wolno; uzasadnienie stoi w src/styles/_brand.scss.
       Kolumna zamiast rzędu, bo znak niesie już nazwę firmy w sobie: podpis stoi POD nim,
       a nie obok. */
    /* PASEK SZUFLADY: znak po lewej, krzyżyk po prawej, kreska pod spodem — układ
       z makiety mobilnej. Rzędem, a nie kolumną, bo dopiero rząd ma prawą krawędź,
       do której krzyżyk się dosuwa; kolumna z podpisem zeszła o poziom niżej, do
       '.brand-mark', i to ona zachowała pionowy porządek „znak, pod nim podpis". */
    .brand-zone {
      display: flex;
      gap: var(--empiria-space-xs);
      align-items: center;
      justify-content: space-between;
      padding: var(--empiria-space-sm);
      border-bottom: 1px solid var(--empiria-brand-accent);
    }

    .brand-mark {
      display: grid;
      gap: var(--empiria-space-2xs);
      justify-items: start;
      min-width: 0;
    }

    /* Wysokość ze skali, szerokość z proporcji pliku ('auto' jest na allowliście kroku 1
       verify:s22-p2). Szuflada ma 240 px, znak przy tej wysokości 124 px — mieści się
       z zapasem, także po odjęciu wyściółki. */
    .brand-zone img,
    .brand img {
      width: auto;
      height: var(--empiria-space-lg);
    }

    .brand-name-secondary {
      color: var(--empiria-brand-text);
      font: var(--mat-sys-label-medium);
    }

    nav {
      padding: var(--empiria-space-sm) var(--empiria-space-xs);
    }

    nav ul {
      display: grid;
      gap: var(--empiria-space-2xs);
      margin: 0;
      padding: 0;
      list-style: none;
    }

    nav a {
      position: relative;
      display: flex;
      gap: var(--empiria-space-xs);
      align-items: center;
      padding: var(--empiria-space-xs) var(--empiria-space-sm);
      border-radius: var(--empiria-radius-md);
      color: var(--mat-sys-on-surface-variant);
      font: var(--mat-sys-label-large);
      text-decoration: none;
      transition:
        background-color var(--empiria-motion-duration) ease-out,
        color var(--empiria-motion-duration) ease-out;
    }

    nav a:hover {
      background: var(--mat-sys-surface-container);
      color: var(--mat-sys-on-surface);
    }

    .nav-icon {
      width: var(--empiria-space-md);
      height: var(--empiria-space-md);
      flex-shrink: 0;
    }

    /* AKTYWNA POZYCJA ROZPOZNAWALNA BEZ KOLORU (WCAG 1.4.1, kryterium manualne 2.10).
       Wypełnienie samo w sobie jest różnicą BARWY, więc niosą ją jeszcze dwie rzeczy
       niezależne od widzenia barw: grubszy krój napisu i pionowy wskaźnik po lewej
       stronie kafla. Wydruk czarno-biały albo tryb wysokiego kontrastu odbiera
       wypełnienie — waga i kształt zostają. */
    nav a.is-active {
      background: var(--mat-sys-primary);
      box-shadow: var(--empiria-elevation-1);
      color: var(--mat-sys-on-primary);
      font-weight: var(--empiria-font-weight-bold);
    }

    nav a.is-active::before {
      position: absolute;
      left: var(--empiria-space-2xs);
      width: var(--empiria-space-2xs);
      height: var(--empiria-space-sm);
      border-radius: var(--empiria-radius-pill);
      background: currentcolor;
      content: '';
    }

    header {
      position: sticky;
      top: 0;
      /* Nad przewijaną treścią, POD skip-linkiem (1001) i pod nakładką szuflady
         Materiala. Krok 2 bramki fazy rozstrzyga tokeny do liczb i wymaga, żeby żadna
         warstwa nie przebiła skip-linku — pierwsza rzecz w tabulacji ma zostać widoczna. */
      z-index: 1;
    }

    mat-toolbar {
      gap: var(--empiria-space-2xs);
      border-bottom: 1px solid var(--mat-sys-outline-variant);
      background: var(--mat-sys-surface);
    }

    /* Dwa selektory, bo pasek miesza dwa źródła glifów: dzwonek to SVG wpisany wprost
       (łapie go potomek 'svg'), a „wyloguj" rysuje 'app-ui-icon', którego wnętrza
       emulowana enkapsulacja nie wpuszcza w zasięg tej reguły — miarę dostaje więc HOST
       komponentu (jego SVG wypełnia hosta w 100%). Bez tego glif liczyłby się z 'em'
       kroju przycisku i stał obok dzwonka w innym rozmiarze. */
    .icon-button svg,
    .icon-button app-ui-icon {
      width: var(--empiria-space-md);
      height: var(--empiria-space-md);
    }

    .brand {
      display: inline-flex;
      align-items: center;
      margin-right: auto;
    }

    /* Na szerokim ekranie szuflada stoi otwarta jako szyna, więc marka w pasku byłaby
       drugim egzemplarzem tego samego napisu. Znika WZROKOWO wraz z nakładką. */
    @include bp.min-md {
      .brand {
        display: none;
      }

      /* Na szerokim ekranie z paska znika i marka, i hamburger (ten drugi razem
         z węzłem, bo szuflada stoi wtedy otwarta), więc po lewej nie zostaje NIC —
         i tak ma być: wyszukiwarka, którą makieta trzyma w tym miejscu, została
         odrzucona jako funkcja niezamawiana. Skróty dosuwa więc do prawej krawędzi
         wyrównanie kontenera, a nie rozpychacz na kontrolce, której już nie ma. */
      mat-toolbar {
        justify-content: flex-end;
      }
    }

    /* Rezerwa pod przyciskiem widgetu dostępności (position: fixed w prawym dolnym
       rogu; WCAG 2.2 SC 2.4.11, i SC 1.4.10 przy 400% powiększenia) WRÓCIŁA do 'main'
       z powrotem: po przeglądzie Filipa stopka ma być niskim paskiem, a rezerwa
       trzymana na niej robiła z niej najwyższy blok strony. Koło widgetu przykrywa na
       dnie scrollera cienki pasek praw autorskich (nieinteraktywny) i ten odstęp — a
       treść, w tym ostatni fokusowalny element, kończy się nad nim. Uzasadnienie
       wysokości sumy tokenów stoi w src/styles.scss. */
    main {
      flex: 1;
      padding: var(--empiria-space-md);
      padding-block-end: calc(var(--empiria-space-3xl) + var(--empiria-space-sm));
    }

    /* Niski, dyskretny pasek (poprawka po przeglądzie Filipa) — sama linijka
       copyright, więc wyściółka pionowa spadła do najmniejszego kroku skali. Barwa
       i kreska jak w layoucie publicznym: jedna stopka, nie dwie interpretacje. */
    footer {
      padding: var(--empiria-space-2xs) var(--empiria-space-md);
      border-top: 1px solid var(--mat-sys-outline-variant);
      background: var(--mat-sys-surface);
    }

    .footer-copyright {
      margin: 0;
      color: var(--mat-sys-on-surface-variant);
      font: var(--mat-sys-body-small);
    }
  `,
})
export class PanelLayoutComponent {
  private readonly session = inject(CustomerSessionService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  private readonly sidenav = viewChild.required(MatSidenav);

  protected readonly panelPath = APP_PATHS.panel;
  // Dashboard panelu (P2): `/panel` przekierowuje na kalendarz, a obok niego stoi
  // Kalendarium — dawny publiczny katalog szkoleń pod nową nazwą.
  protected readonly calendarPath = APP_PATHS.calendar;
  protected readonly calendariumPath = APP_PATHS.calendarium;
  protected readonly myCoursesPath = APP_PATHS.myCourses;
  protected readonly myRegistrationsPath = APP_PATHS.myRegistrations;
  protected readonly certificatesPath = APP_PATHS.certificates;
  // BEZWARUNKOWA, jak trzy pozycje nad nią: ekran „Mój profil" (S-12) istnieje dla
  // obu typów konta, więc nie ma tu czego chować — w odróżnieniu od abonamentów i
  // zapotrzebowania, które dla klienta indywidualnego kończą się 403.
  protected readonly myProfilePath = APP_PATHS.myProfile;
  // BEZWARUNKOWA, jak pozycje nad nią: centrum powiadomień (S-16) dostaje każde konto
  // panelu — zdarzenia, o których mówi (przyjęte zgłoszenie, gotowe zaświadczenie),
  // zdarzają się i osobie prywatnej, i placówce.
  protected readonly notificationsPath = APP_PATHS.notifications;
  protected readonly mySubscriptionsPath = APP_PATHS.mySubscriptions;
  protected readonly closedCourseRequestPath = APP_PATHS.closedCourseRequest;
  // BEZWARUNKOWA: dane biura ogląda każde konto (P2 przeniósł ekran z części
  // publicznej do panelu bez zmiany jego treści).
  protected readonly contactPath = APP_PATHS.contact;

  /**
   * Nazwa spółki do rzędu praw autorskich (P3) — z jedynej kanonicznej stałej
   * (S-17), nigdy przepisana.
   */
  protected readonly contact = EMPIRIA_CONTACT;

  /**
   * Rok w prawach autorskich liczony w RUNTIME, nie wpisany — panel stoi w całości
   * na `RenderMode.Client`, więc wartość powstaje u klienta przy każdym wejściu,
   * a rok na sztywno kłamałby od 1 stycznia (ten sam powód co w layoucie publicznym).
   */
  protected readonly currentYear = new Date().getFullYear();

  private readonly customerProfile = inject(CustomerProfileService);
  private readonly customerType = this.customerProfile.customerType;

  constructor() {
    // One profile request per PANEL SESSION (S-11 impl-review F1): the shell is
    // rebuilt on every entry into the panel, while the service is a root singleton —
    // so the ask has to live here, not in the service's constructor.
    this.customerProfile.ensureLoaded();
  }

  /**
   * Establishments only, and `null` („nie wiadomo") counts as NOT an establishment:
   * an entry shown on a guess would bounce off the backend's 403, while a hidden one
   * costs an establishment nothing it cannot recover by reloading the panel.
   */
  protected readonly showSubscriptions = computed(
    () => this.customerType() === CUSTOMER_TYPE.establishment,
  );

  /**
   * S-10, same rule and same source as `showSubscriptions` (one profile request for
   * the whole sidenav): the closed-course request form is an establishment feature
   * (Q-63/A). "In flight" and "failed" both render as `false` — a menu entry that
   * appears after a moment beats one that appears and vanishes for a screen reader.
   * The actual boundary is `establishmentOnlyGuard` on the route plus the backend's
   * 403, never this visibility flag.
   */
  protected readonly showClosedCourseRequest = computed(
    () => this.customerType() === CUSTOMER_TYPE.establishment,
  );

  private readonly isHandset = toSignal(
    inject(BreakpointObserver)
      .observe(Breakpoints.Handset)
      .pipe(map((state) => state.matches)),
    { initialValue: false },
  );

  protected readonly sidenavMode = computed(() => (this.isHandset() ? 'over' : 'side'));

  // linkedSignal: user toggles freely, but a breakpoint change resets to the
  // sensible default (open as a side rail on desktop, closed as an overlay on mobile).
  protected readonly sidenavOpened = linkedSignal(() => !this.isHandset());

  protected toggleSidenav(): void {
    this.sidenavOpened.update((opened) => !opened);
  }

  /**
   * Krzyżyk w pasku szuflady. ZAMYKA, nie przełącza — kontrolka nazwana „Zamknij" ma
   * robić dokładnie to, co mówi jej nazwa, także wtedy, gdy stan zdąży się zmienić
   * między narysowaniem a kliknięciem (przełącznik otworzyłby wtedy szufladę z powrotem).
   *
   * Fokusu tu nie ruszamy i to nie jest przeoczenie: `MatDrawer` w trybie `over`
   * przywraca go sam na element, z którego szuflada została otwarta, czyli na hamburger
   * w pasku. Ustawienie fokusu ręcznie ścigałoby się z tamtym przywróceniem.
   */
  protected closeSidenav(): void {
    this.sidenavOpened.set(false);
  }

  /**
   * On a phone the sidenav is an overlay, so following a link would leave the
   * drawer covering the content the customer just asked for — and leave keyboard
   * focus inside a hidden region, which is the WCAG half of the problem. Focus
   * moves to `<main>` only after the drawer has actually closed; on desktop the
   * rail stays put and focus is left alone.
   */
  protected handleNavigation(main: HTMLElement): void {
    if (this.sidenavMode() !== 'over') {
      return;
    }

    void this.sidenav()
      .close()
      .then(() => {
        main.focus();
      });
  }

  // The backend logout is idempotent and the service drops the local session
  // either way, so navigation happens once the call settles — never before.
  protected logout(): void {
    // Navigation below happens WITHOUT a page reload, so the same application
    // instance lives on. A cached account type or profile would then answer for
    // the PREVIOUS customer after logging into another account — and
    // `establishmentOnlyGuard` (Q-63/A) decides on that very value
    // (S-11 impl-review F1). The reason still stands; what moved is the PLACE it is
    // enforced in: since S-15 phase 3 both caches are cleared by
    // `CustomerSessionService.dropSession()`, i.e. on every road out of a session —
    // including the account-deletion screen, which logs out with this component
    // nowhere on screen.
    this.session
      .logout()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        void this.router.navigate([APP_PATHS.login]);
      });
  }

  protected skipToContent(event: Event, target: HTMLElement): void {
    event.preventDefault();
    target.focus();
  }
}
